import { useEffect, useRef, useState } from 'react';
import { Typography } from '@mui/material';
import PropTypes from 'prop-types';
import { homeColors, homeEase, sectionTitleSx } from '../style/homeStyles';

const splitWords = (text, isHighlighted) => text
  .split(/\s+/)
  .filter((word) => word.length > 0)
  .map((word) => ({ word, isHighlighted }));

// Título que entra palabra por palabra (fade + slide + desenfoque), escalonado.
const AnimatedTitle = ({
  text, highlight, component, fontSize, stagger, sx,
}) => {
  const ref = useRef(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    const element = ref.current;
    if (!element) return undefined;

    if (typeof IntersectionObserver === 'undefined') {
      setActive(true);
      return undefined;
    }

    const observer = new IntersectionObserver((entries) => {
      entries.forEach((entry) => {
        if (entry.isIntersecting) {
          setActive(true);
          observer.unobserve(element);
        }
      });
    }, { threshold: 0.2 });

    observer.observe(element);
    return () => observer.disconnect();
  }, []);

  const words = [...splitWords(highlight, true), ...splitWords(text, false)];

  return (
    <Typography
      ref={ref}
      component={component}
      sx={{ ...sectionTitleSx, fontSize, ...sx }}
    >
      {words.map((item, index) => (
        <Typography
          // Las palabras pueden repetirse dentro del mismo título, el índice las desambigua.
          // eslint-disable-next-line react/no-array-index-key
          key={`${item.word}-${index}`}
          component="span"
          sx={{
            display: 'inline-block',
            font: 'inherit',
            color: item.isHighlighted ? homeColors.pink : 'inherit',
            opacity: active ? 1 : 0,
            transform: active ? 'translateY(0)' : 'translateY(16px)',
            filter: active ? 'blur(0)' : 'blur(4px)',
            transition: `opacity .65s ${homeEase}, transform .8s ${homeEase}, filter .65s ${homeEase}`,
            transitionDelay: `${index * stagger}ms`,
            willChange: 'opacity, transform, filter',
            '@media (prefers-reduced-motion: reduce)': {
              opacity: 1,
              transform: 'none',
              filter: 'none',
              transition: 'none',
            },
          }}
        >
          {item.word}
          &nbsp;
        </Typography>
      ))}
    </Typography>
  );
};

AnimatedTitle.propTypes = {
  text: PropTypes.string.isRequired,
  // Palabras que van en magenta delante del texto.
  highlight: PropTypes.string,
  component: PropTypes.string,
  fontSize: PropTypes.oneOfType([PropTypes.number, PropTypes.string]),
  stagger: PropTypes.number,
  sx: PropTypes.shape({}),
};

AnimatedTitle.defaultProps = {
  highlight: '',
  component: 'h2',
  fontSize: 28,
  stagger: 55,
  sx: {},
};

export default AnimatedTitle;
